import { createFileRoute, useParams } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import type { FermentationProcedureId } from "@brewbase/db/types";

type KpiFormula = RouterOutputs["kpis"]["listProcedureFormulas"][number];
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { FormulaInspector } from "@/features/kpi-admin/components/formula-inspector";
import { FormulaEditor } from "@/features/kpi-admin/components/formula-editor";
import { Calculator, Plus, Trash2, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute(
  "/fermentations/procedures/$procedure_id/kpis",
)({
  component: KpisPage,
});

function toSnakeCase(str: string): string {
  return str
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function KpisPage() {
  const { procedure_id } = useParams({ from: Route.id });
  const procedureId = procedure_id as unknown as FermentationProcedureId;
  const procedureIdNum = procedureId as unknown as number;
  const utils = trpc.useUtils();

  const { data: procedure, isLoading: isLoadingProcedure } =
    trpc.fermentationProcedures.getWithDetails.useQuery(procedureId);
  const { data: formulas, isLoading: isLoadingFormulas } =
    trpc.kpis.listProcedureFormulas.useQuery(procedureIdNum);
  const { data: templates } = trpc.kpis.listTemplates.useQuery();
  const { data: recomputationScope } =
    trpc.kpis.getRecomputationScope.useQuery(procedureIdNum);

  const [selectedFormulaId, setSelectedFormulaId] = useState<number | null>(
    null,
  );
  const [newFormulaDialogOpen, setNewFormulaDialogOpen] = useState(false);

  const isLocked = procedure?.is_locked ?? false;

  const sortedFormulas = useMemo(() => {
    if (!formulas) return [];
    return [...formulas].sort((a, b) => {
      const orderA = a.display_order ?? 0;
      const orderB = b.display_order ?? 0;
      if (orderA !== orderB) return orderA - orderB;
      return (a.id as unknown as number) - (b.id as unknown as number);
    });
  }, [formulas]);

  const availableTemplates = useMemo(() => {
    if (!templates || !formulas) return [];
    const activeSlugs = new Set(formulas.map((f) => f.slug));
    return templates.filter((t) => !activeSlugs.has(t.slug));
  }, [templates, formulas]);

  const instantiateMutation = trpc.kpis.instantiateTemplate.useMutation({
    onSuccess: () => {
      utils.kpis.listProcedureFormulas.invalidate(procedureIdNum);
      // The server backfills KPI values for the new formula; refresh the value
      // surfaces so it shows up (recompute is async — may lag briefly).
      utils.kpis.getKpisWithMetadata.invalidate();
      utils.kpis.getKpisForFermentation.invalidate();
      toast.success("Formula added from template");
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = trpc.kpis.deleteFormula.useMutation({
    onSuccess: () => {
      utils.kpis.listProcedureFormulas.invalidate(procedureIdNum);
      if (selectedFormulaId) {
        setSelectedFormulaId(null);
      }
      toast.success("Formula deleted");
    },
    onError: (err) => toast.error(err.message),
  });

  const handleChanged = () => {
    utils.kpis.listProcedureFormulas.invalidate(procedureIdNum);
    // A kind change recomputes values server-side; refresh the value surfaces.
    utils.kpis.getKpisWithMetadata.invalidate();
    utils.kpis.getKpisForFermentation.invalidate();
  };

  if (isLoadingProcedure || isLoadingFormulas) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!procedure) return null;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2 border-b flex-shrink-0">
        <Calculator className="w-4 h-4 text-olive-11" />
        <span className="text-sm font-medium">KPIs</span>
        <span className="text-xs text-olive-9">
          {sortedFormulas.length} formula(s)
        </span>
      </div>

      {/* Content */}
      {sortedFormulas.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-olive-9">
          <Calculator className="w-8 h-8" />
          <p className="text-sm">No KPI formulas attached to this procedure</p>
          {!isLocked && (
            <div className="flex items-center gap-2">
              <AddFromTemplatePopover
                availableTemplates={availableTemplates}
                onAdd={(templateId) =>
                  instantiateMutation.mutate({
                    templateId,
                    procedureId: procedureIdNum,
                  })
                }
                isPending={instantiateMutation.isPending}
              />
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-sm"
                onClick={() => setNewFormulaDialogOpen(true)}
              >
                <Plus className="w-4 h-4" />
                New Custom
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex">
          {/* Left: Formula list */}
          <div className="w-5/12 min-h-0 border-r flex flex-col">
            <ScrollArea className="flex-1">
              <div className="divide-y">
                {sortedFormulas.map((formula) => (
                  <FormulaRow
                    key={formula.id as unknown as number}
                    formula={formula}
                    selected={
                      selectedFormulaId ===
                      (formula.id as unknown as number)
                    }
                    onClick={() =>
                      setSelectedFormulaId(
                        formula.id as unknown as number,
                      )
                    }
                    onDelete={() =>
                      deleteMutation.mutate(
                        formula.id as unknown as number,
                      )
                    }
                    isDeleting={deleteMutation.isPending}
                    readOnly={isLocked}
                  />
                ))}
              </div>
              {!isLocked && (
                <div className="flex items-center gap-1 px-3 py-2 border-t">
                  <AddFromTemplatePopover
                    availableTemplates={availableTemplates}
                    onAdd={(templateId) =>
                      instantiateMutation.mutate({
                        templateId,
                        procedureId: procedureIdNum,
                      })
                    }
                    isPending={instantiateMutation.isPending}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1.5 text-sm"
                    onClick={() => setNewFormulaDialogOpen(true)}
                  >
                    <Plus className="w-4 h-4" />
                    New Custom
                  </Button>
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Right: Inspector */}
          <div className="w-7/12 min-h-0">
            {selectedFormulaId ? (
              <FormulaInspector
                key={selectedFormulaId}
                formulaId={selectedFormulaId}
                owner={{ procedureId: procedureIdNum }}
                recomputationScope={recomputationScope}
                readOnly={isLocked}
                onClose={() => setSelectedFormulaId(null)}
                onChanged={handleChanged}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-olive-9">
                Select a formula to edit
              </div>
            )}
          </div>
        </div>
      )}

      {/* New formula dialog */}
      <NewProcedureFormulaDialog
        procedureId={procedureIdNum}
        open={newFormulaDialogOpen}
        onOpenChange={setNewFormulaDialogOpen}
        onSuccess={(formulaId) => {
          utils.kpis.listProcedureFormulas.invalidate(procedureIdNum);
          setSelectedFormulaId(formulaId);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// FormulaRow
// ---------------------------------------------------------------------------

function FormulaRow({
  formula,
  selected,
  onClick,
  onDelete,
  isDeleting,
  readOnly,
}: {
  formula: KpiFormula;
  selected: boolean;
  onClick: () => void;
  onDelete: () => void;
  isDeleting: boolean;
  readOnly: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 cursor-pointer group ${
        selected ? "bg-olive-3" : "hover:bg-olive-2"
      }`}
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <code className="text-xs font-mono text-olive-12 truncate">
            {formula.slug}
          </code>
          {formula.kind === "summary" && (
            <Badge variant="outline" className="text-xs shrink-0">
              summary
            </Badge>
          )}
          {formula.source_template_id && (
            <Badge variant="secondary" className="text-xs shrink-0">
              template
            </Badge>
          )}
        </div>
        <div className="text-xs text-olive-9 truncate">{formula.name}</div>
        <div className="text-xs text-olive-8 font-mono truncate mt-0.5">
          {formula.expression}
        </div>
      </div>
      {!readOnly && (
        <div className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <DeleteConfirmDialog
            title="Delete Formula"
            description={`Delete "${formula.name}"? This cannot be undone.`}
            onConfirm={onDelete}
            isDeleting={isDeleting}
            trigger={
              <button
                className="p-1 text-olive-9 hover:text-red-600"
                onClick={(e) => e.stopPropagation()}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            }
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddFromTemplatePopover
// ---------------------------------------------------------------------------

function AddFromTemplatePopover({
  availableTemplates,
  onAdd,
  isPending,
}: {
  availableTemplates: Array<{ id: unknown; name: string; slug: string }>;
  onAdd: (templateId: number) => void;
  isPending: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-sm">
          <LayoutTemplate className="w-4 h-4" />
          Add from Template
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="p-3 border-b">
          <span className="text-sm font-medium">Available Templates</span>
        </div>
        <div className="overflow-y-auto max-h-[min(60vh,400px)]">
          {availableTemplates.length === 0 ? (
            <div className="p-4 text-xs text-olive-9 text-center">
              All templates have been added
            </div>
          ) : (
            <div className="p-2">
              {availableTemplates.map((template) => (
                <div
                  key={template.id as unknown as number}
                  className="flex items-center gap-2 px-1 py-1"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{template.name}</div>
                    <code className="text-xs text-olive-9 font-mono">
                      {template.slug}
                    </code>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs px-2 shrink-0"
                    disabled={isPending}
                    onClick={() => {
                      onAdd(template.id as unknown as number);
                      setOpen(false);
                    }}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// NewProcedureFormulaDialog
// ---------------------------------------------------------------------------

function NewProcedureFormulaDialog({
  procedureId,
  open,
  onOpenChange,
  onSuccess,
}: {
  procedureId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (formulaId: number) => void;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [expression, setExpression] = useState("");
  const [kind, setKind] = useState<"kpi_timepoint" | "summary" | "derived">(
    "kpi_timepoint",
  );

  const createMutation = trpc.kpis.createProcedureFormula.useMutation({
    onSuccess: (data) => {
      toast.success(`Formula "${name}" created.`);
      onOpenChange(false);
      setName("");
      setSlug("");
      setSlugTouched(false);
      setExpression("");
      setKind("kpi_timepoint");
      onSuccess(data.id as unknown as number);
    },
    onError: (error) => {
      toast.error(`Failed to create formula: ${error.message}`);
    },
  });

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugTouched) {
      setSlug(toSnakeCase(value));
    }
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error("Name is required.");
      return;
    }
    if (!slug.trim()) {
      toast.error("Slug is required.");
      return;
    }
    if (!expression.trim()) {
      toast.error("Expression is required.");
      return;
    }
    createMutation.mutate({
      procedureId,
      name: name.trim(),
      slug: slug.trim(),
      expression: expression.trim(),
      kind,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>New Custom Formula</DialogTitle>
          <DialogDescription>
            Create a procedure-owned KPI formula.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="proc-formula-name" className="text-right">
              Name
            </Label>
            <Input
              id="proc-formula-name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="col-span-3"
              placeholder="e.g., Custom Yield"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="proc-formula-slug" className="text-right">
              Slug
            </Label>
            <Input
              id="proc-formula-slug"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugTouched(true);
              }}
              className="col-span-3 font-mono text-sm"
              placeholder="e.g., custom_yield"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label className="text-right">Kind</Label>
            <Select
              value={kind}
              onValueChange={(v) =>
                setKind(v as "kpi_timepoint" | "summary" | "derived")
              }
            >
              <SelectTrigger className="col-span-3 h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="kpi_timepoint">Timepoint KPI</SelectItem>
                <SelectItem value="summary">Summary KPI</SelectItem>
                <SelectItem value="derived">Derived</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-sm">Expression</Label>
            <FormulaEditor value={expression} onChange={setExpression} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create Formula"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
